import {
  coerceDateTimeLocal,
  snowflakeId,
  toApiDateTime,
} from '../journey-accommodation/accommodationModel';
import { coerceDateTime } from '../my-journeys/myJourneysModel';
import { BUDGET_CATEGORY_CODES, budgetCategoryLabel } from '../finance/budgetCategory';
import { memberDisplayName } from '../member-display/memberDisplayModel';
import {
  compareMoney,
  divideMoneyByInteger,
  formatMoneyDisplay,
  isPositiveMoney,
  moneyToCanonicalString,
  sumMoneyStrings,
} from '../finance/financeMoney';

/** Expense records split input snapshot only — calculation is a separate endpoint. */
export const EXPENSE_SPLIT_METHODS = Object.freeze([
  'EQUAL',
  'EXACT_AMOUNT',
  'PERCENTAGE',
  'SHARES',
  'PAYER_ONLY',
]);

export const EXPENSE_SPLIT_METHOD_LABELS = Object.freeze({
  EQUAL: '均分',
  EXACT_AMOUNT: '指定金額',
  PERCENTAGE: '百分比',
  SHARES: '份數',
  PAYER_ONLY: '僅付款人',
});

export const DEFAULT_EXPENSE_SPLIT_METHOD = 'EQUAL';

export const EXPENSE_STATUS_LABELS = Object.freeze({
  ACTIVE: '進行中',
  VOIDED: '已作廢',
});

export const EXPENSE_STATUS_FILTERS = Object.freeze(['ALL', 'ACTIVE', 'VOIDED']);

export function expenseStatusLabel(status) {
  const key = String(status || '');
  return EXPENSE_STATUS_LABELS[key] || key || '—';
}

export function classifyExpenseError(cause) {
  const code = cause?.code ?? cause?.raw?.code;
  const key = String(cause?.raw?.key || cause?.message || '');
  if (code === 90003 || code === 90005 || code === 90006
      || key.includes('token.not.found') || key.includes('token.illegal') || key.includes('token.expired')) {
    return 'unauthorized';
  }
  if (code === 92001 || key === 'travel.journey.not.found') return 'journey-not-found';
  if (code === 93001 && key.includes('budget.not.found')) return 'budget-not-found';
  if (code === 93001 || key.includes('planning.not.found')) return 'not-found';
  if (code === 92002 || code === 93002 || key.includes('permission.denied')) return 'forbidden';
  if (code === 93003 && key.includes('concurrent.modification')) return 'conflict';
  if (code === 93003 && key.includes('idempotency.conflict')) return 'idempotency-conflict';
  if (code === 93003 && key.includes('expense.voided')) return 'voided';
  if (code === 93003 && key.includes('lifecycle')) return 'lifecycle';
  if (code === 93003 || key.includes('concurrent')) return 'conflict';
  if (code === 92000 || code === 93000 || key.includes('validation') || key.includes('currency')) {
    return 'validation';
  }
  if (code === 93004 || key === 'travel.planning.im.unavailable') return 'unavailable';
  return 'error';
}

export const EXPENSE_CHAT_SHARE_CONFIRM = '要把這筆支出的摘要分享到旅程聊天室嗎？';

const SHARE_LIFECYCLES = Object.freeze(['READY', 'TRAVELING', 'COMPLETED']);

/** ACTIVE Journey member may share a readable Expense snapshot. DRAFT cannot ensure Journey Chat. */
export function canShareExpenseToChat({ role, lifecycleStatus } = {}) {
  if (!SHARE_LIFECYCLES.includes(lifecycleStatus)) return false;
  return role === 'OWNER' || role === 'COORDINATOR' || role === 'MEMBER';
}

export function normalizeExpenseParticipant(value) {
  return {
    participantId: snowflakeId(value?.participantId),
    memberId: snowflakeId(value?.memberId),
    splitMethod: value?.splitMethod == null ? null : String(value.splitMethod),
    splitMethodValue: value?.splitMethodValue == null
      ? null
      : (moneyToCanonicalString(value.splitMethodValue) || String(value.splitMethodValue)),
    displayOrder: Number(value?.displayOrder) || 0,
  };
}

export function normalizeExpenseListItem(value) {
  return {
    expenseId: snowflakeId(value?.expenseId),
    journeyId: snowflakeId(value?.journeyId),
    recordedByMemberId: snowflakeId(value?.recordedByMemberId),
    payerMemberId: snowflakeId(value?.payerMemberId),
    title: String(value?.title || ''),
    amount: moneyToCanonicalString(value?.amount) || '0.00000000',
    currencyCode: String(value?.currencyCode || '').toUpperCase(),
    categoryCode: String(value?.categoryCode || ''),
    occurredAt: coerceDateTime(value?.occurredAt),
    status: String(value?.status || ''),
    statusLabel: expenseStatusLabel(value?.status),
  };
}

export function normalizeExpenseDetail(value) {
  const participants = Array.isArray(value?.participants)
    ? value.participants.map(normalizeExpenseParticipant)
    : [];
  return {
    ...normalizeExpenseListItem(value),
    budgetId: snowflakeId(value?.budgetId),
    budgetRowVersion: value?.budgetRowVersion == null ? null : String(value.budgetRowVersion),
    note: value?.note == null ? '' : String(value.note),
    splitMethod: value?.splitMethod == null ? null : String(value.splitMethod),
    voidedAt: coerceDateTime(value?.voidedAt),
    voidedByMemberId: snowflakeId(value?.voidedByMemberId),
    voidReason: value?.voidReason == null ? '' : String(value.voidReason),
    createTime: coerceDateTime(value?.createTime),
    updateTime: coerceDateTime(value?.updateTime),
    participants,
    participantCount: participants.length,
    noOp: value?.noOp == null ? null : Boolean(value.noOp),
  };
}

export function normalizeExpenseSummary(value) {
  return {
    journeyId: snowflakeId(value?.journeyId),
    totalExpenseCount: Number(value?.totalExpenseCount) || 0,
    activeExpenseCount: Number(value?.activeExpenseCount) || 0,
    voidedExpenseCount: Number(value?.voidedExpenseCount) || 0,
    totalAmount: moneyToCanonicalString(value?.totalAmount) || '0.00000000',
    currency: value?.currency == null ? null : String(value.currency).toUpperCase(),
  };
}

export function normalizeExpenseList(payload) {
  if (!Array.isArray(payload)) return [];
  return payload.map(normalizeExpenseListItem);
}

export function activeJourneyMembers(members) {
  return (members || []).filter((member) => member?.membershipStatus === 'ACTIVE');
}

export function memberDisplayLabel(member, summariesById) {
  if (!member) return '—';
  const id = snowflakeId(member.memberId);
  const roleLabel = ({
    OWNER: '擁有者',
    COORDINATOR: '協調者',
    MEMBER: '旅伴',
  })[member.role] || '';
  const role = roleLabel ? `（${roleLabel}）` : '';
  if (!id) return `—${role}`;
  // 產品顯示不外露 memberId／短碼
  return `${memberDisplayName(id, summariesById)}${role}`;
}

export function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `exp-${Date.now()}-${Math.random().toString(16).slice(2, 10)}`;
}

export function emptyExpenseDraft({
  currencyCode = 'TWD',
  payerMemberId = '',
  participantMemberIds = [],
} = {}) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const occurredAt = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  return {
    title: '',
    note: '',
    amount: '',
    currencyCode: String(currencyCode || 'TWD').toUpperCase(),
    categoryCode: 'FOOD',
    payerMemberId: payerMemberId == null ? '' : String(payerMemberId),
    occurredAt,
    splitMethod: DEFAULT_EXPENSE_SPLIT_METHOD,
    participantMemberIds: (participantMemberIds || []).map(String),
    participantValues: {},
  };
}

export function draftFromExpense(expense) {
  const participantValues = {};
  (expense?.participants || []).forEach((row) => {
    const id = String(row.memberId);
    if (row.splitMethodValue != null && row.splitMethodValue !== '') {
      participantValues[id] = formatMoneyDisplay(row.splitMethodValue);
    }
  });
  return {
    title: expense?.title || '',
    note: expense?.note || '',
    amount: formatMoneyDisplay(expense?.amount || '0'),
    currencyCode: String(expense?.currencyCode || '').toUpperCase(),
    categoryCode: expense?.categoryCode || 'FOOD',
    payerMemberId: expense?.payerMemberId == null ? '' : String(expense.payerMemberId),
    occurredAt: coerceDateTimeLocal(expense?.occurredAt),
    splitMethod: expense?.splitMethod || DEFAULT_EXPENSE_SPLIT_METHOD,
    participantMemberIds: (expense?.participants || []).map((row) => String(row.memberId)),
    participantValues,
  };
}

function resolveParticipantIds(draft) {
  const method = draft?.splitMethod || DEFAULT_EXPENSE_SPLIT_METHOD;
  if (method === 'PAYER_ONLY') {
    const payer = draft?.payerMemberId == null ? '' : String(draft.payerMemberId);
    return payer ? [payer] : [];
  }
  return (draft?.participantMemberIds || []).map(String);
}

function buildParticipantPayload(draft) {
  const method = draft?.splitMethod || DEFAULT_EXPENSE_SPLIT_METHOD;
  const ids = resolveParticipantIds(draft);
  const values = draft?.participantValues || {};
  return ids.map((memberId, index) => {
    let splitMethodValue = null;
    if (method === 'EXACT_AMOUNT' || method === 'PERCENTAGE' || method === 'SHARES') {
      const raw = values[memberId];
      splitMethodValue = moneyToCanonicalString(raw);
    }
    return {
      memberId: Number(memberId),
      splitMethodValue,
      displayOrder: index + 1,
    };
  });
}

export function buildExpenseCreatePayload(draft, {
  expectedBudgetRowVersion,
  clientRequestId,
} = {}) {
  return {
    expectedBudgetRowVersion: Number(expectedBudgetRowVersion),
    clientRequestId: String(clientRequestId || '').trim(),
    payerMemberId: Number(draft?.payerMemberId),
    title: String(draft?.title || '').trim(),
    note: draft?.note == null || String(draft.note).trim() === '' ? null : String(draft.note).trim(),
    amount: moneyToCanonicalString(draft?.amount),
    currencyCode: String(draft?.currencyCode || '').trim().toUpperCase(),
    categoryCode: draft?.categoryCode,
    occurredAt: toApiDateTime(draft?.occurredAt),
    splitMethod: draft?.splitMethod || DEFAULT_EXPENSE_SPLIT_METHOD,
    participants: buildParticipantPayload(draft),
    confirm: true,
  };
}

export function buildExpenseUpdatePayload(draft, { expectedBudgetRowVersion } = {}) {
  const body = buildExpenseCreatePayload(draft, {
    expectedBudgetRowVersion,
    clientRequestId: 'unused',
  });
  delete body.clientRequestId;
  return body;
}

export function buildExpenseVoidPayload({ expectedBudgetRowVersion, reason } = {}) {
  return {
    expectedBudgetRowVersion: Number(expectedBudgetRowVersion),
    reason: String(reason || '').trim(),
    confirm: true,
  };
}

export function validateExpenseDraft(draft, { requireParticipants = true } = {}) {
  const title = String(draft?.title || '').trim();
  if (!title) return '請填寫支出標題。';
  if (title.length > 100) return '標題最多 100 字。';
  if (!isPositiveMoney(draft?.amount)) return '金額須為正數小數。';
  const currency = String(draft?.currencyCode || '').trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) return '幣別須與旅行預算一致。';
  if (!BUDGET_CATEGORY_CODES.includes(draft?.categoryCode)) return '請選擇類別。';
  if (!draft?.payerMemberId) return '請選擇付款人。';
  if (!toApiDateTime(draft?.occurredAt)) return '請填寫發生時間。';
  const method = draft?.splitMethod || '';
  if (!EXPENSE_SPLIT_METHODS.includes(method)) {
    return '請選擇分攤方式。';
  }
  const participants = resolveParticipantIds(draft);
  if (requireParticipants && participants.length === 0) {
    return '請至少選擇一位分攤對象。';
  }
  if (method === 'PAYER_ONLY') {
    if (participants.length !== 1 || String(participants[0]) !== String(draft.payerMemberId)) {
      return '僅付款人分攤時，分攤對象須為付款人本人。';
    }
  }
  if (method === 'EXACT_AMOUNT') {
    const values = participants.map((id) => draft?.participantValues?.[id]);
    if (values.some((value) => !isPositiveMoney(value))) {
      return '指定金額須皆為正數。';
    }
    const sum = sumMoneyStrings(values);
    if (compareMoney(sum, draft.amount) !== 0) {
      return '指定金額加總須等於支出金額。';
    }
  }
  if (method === 'PERCENTAGE') {
    const values = participants.map((id) => draft?.participantValues?.[id]);
    if (values.some((value) => !isPositiveMoney(value))) {
      return '百分比須皆為正數。';
    }
    if (values.some((value) => compareMoney(value, '100') > 0)) {
      return '單一百分比不可超過 100。';
    }
    const sum = sumMoneyStrings(values);
    if (compareMoney(sum, '100') !== 0) {
      return '百分比加總須等於 100。';
    }
  }
  if (method === 'SHARES') {
    const values = participants.map((id) => draft?.participantValues?.[id]);
    if (values.some((value) => !isPositiveMoney(value))) {
      return '份數須皆為正數。';
    }
  }
  const note = draft?.note == null ? '' : String(draft.note);
  if (note.length > 1000) return '備註最多 1000 字。';
  return '';
}

/** EQUAL 均分預覽（僅 UX 提示；最終以伺服器結果為準）。 */
export function equalSplitPreview(amount, participantCount) {
  const share = divideMoneyByInteger(amount, participantCount);
  if (!share) return null;
  return {
    participantCount: Number(participantCount),
    shareAmount: share,
    shareDisplay: formatMoneyDisplay(share),
  };
}

export function expenseSplitMethodLabel(method) {
  const key = String(method || '');
  return EXPENSE_SPLIT_METHOD_LABELS[key] || key || '—';
}

export function canRecordExpense({ role, lifecycleStatus, payerMemberId, viewerMemberId }) {
  const writable = ['DRAFT', 'READY', 'TRAVELING', 'COMPLETED'].includes(lifecycleStatus);
  if (!writable) return false;
  if (role === 'OWNER' || role === 'COORDINATOR') return true;
  if (role === 'MEMBER') {
    return String(payerMemberId) === String(viewerMemberId);
  }
  return false;
}

export function canMutateExpense({ role, lifecycleStatus, recordedByMemberId, viewerMemberId }) {
  const writable = ['DRAFT', 'READY', 'TRAVELING', 'COMPLETED'].includes(lifecycleStatus);
  if (!writable) return false;
  if (role === 'OWNER' || role === 'COORDINATOR') return true;
  if (role === 'MEMBER') {
    return String(recordedByMemberId) === String(viewerMemberId);
  }
  return false;
}

export { budgetCategoryLabel, formatMoneyDisplay, moneyToCanonicalString, snowflakeId };
